// Keep one request in flight and debounce each field independently.
export function createAccountAutosave(initial, { save, validate, changed, delay = 700 }) {
    const values = structuredClone(initial);
    const saved = structuredClone(initial);
    const timers = new Map();
    const ready = new Set();
    const errors = new Map();
    let running = null;
    let disposed = false;
    let edited = false;
    const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const dirty = () => Object.keys(values).some(key => !equal(values[key], saved[key]));
    function notify() {
        if (!disposed) changed({ saving: !!running, dirty: dirty(), edited, error: [...errors.values()].join(' ') });
    }
    async function drain() {
        if (running) return running;
        running = (async () => {
            // Yield so running is assigned even when there are no ready fields.
            await Promise.resolve();
            while (ready.size && !disposed) {
                const key = ready.values().next().value;
                ready.delete(key);
                if (equal(values[key], saved[key])) { errors.delete(key); continue; }
                const value = structuredClone(values[key]);
                const invalid = validate(key, value);
                if (invalid) { errors.set(key, invalid); continue; }
                try {
                    await save({ [key]: value });
                    saved[key] = value;
                    errors.delete(key);
                } catch (error) {
                    errors.set(key, error.message);
                }
            }
        })();
        notify();
        await running;
        running = null;
        notify();
    }
    return {
        update(next) {
            for (const [key, value] of Object.entries(next)) {
                if (equal(values[key], value)) continue;
                values[key] = structuredClone(value);
                edited = true;
                errors.delete(key);
                clearTimeout(timers.get(key));
                ready.delete(key);
                if (typeof value === 'boolean') { ready.add(key); }
                else timers.set(key, setTimeout(() => { timers.delete(key); ready.add(key); void drain(); }, delay));
            }
            notify();
            if (ready.size) void drain();
        },
        async flush() {
            for (const timer of timers.values()) clearTimeout(timer);
            timers.clear();
            // Compare against the result of the in-flight save, not its old baseline.
            if (running) await drain();
            for (const key of Object.keys(values)) if (!equal(values[key], saved[key])) ready.add(key);
            await drain();
            // A flush can join an older request; drain any values queued during it.
            if (ready.size) await drain();
            return !dirty();
        },
        isDirty: dirty,
        dispose() { disposed = true; for (const timer of timers.values()) clearTimeout(timer); },
    };
}
